import { useTenant } from "@/providers/session";
import {
  type ApolloError,
  type FetchResult,
  gql,
  useMutation,
} from "@apollo/client";
import { noop } from "lodash";

type ConnectBankInput = {
  bankId: number;
  txnImportEnabled: boolean;
  startImportDate: Date;
  connectionName?: string;
  interfaces?: ("FINTS_SERVER" | "WEB_SCRAPER" | "XS2A")[];
};

type ConnectBankResponse = {
  connectToBank: {
    url: string;
    id: string;
  };
};

export const useConnectBank = (
  { config } = {
    config: {
      toasts: {
        success: true,
        loading: true,
      },
    },
  },
): [
  (
    data: ConnectBankInput,
    onSuccess?: () => void,
    onError?: () => void,
  ) => Promise<{ url: string; id: string }>,
  {
    data:
      | {
          url: string;
          id: string;
        }
      | undefined;
    loading: boolean;
    error?: ApolloError;
  },
] => {
  const { tenantId } = useTenant();
  const [connectBank, { data, loading, error }] =
    useMutation<ConnectBankResponse>(CONNECT_BANK_MUTATION);

  const handleConnect = async (
    data: ConnectBankInput,
    onSuccess: () => void = noop,
    onError: () => void = noop,
  ) => {
    try {
      const response = await connectBank({
        variables: {
          bankId: data.bankId,
          tenantId,
          txnImportEnabled: data.txnImportEnabled,
          startImportDate: data.startImportDate,
          connectionName: data.connectionName,
          interfaces: data.interfaces,
        },
      });

      if (response.errors || !response.data) {
        throw new Error("error connecting to bank");
      }

      onSuccess();

      return response.data.connectToBank;
    } catch (error) {
      onError();
      throw error;
    }
  };

  return [handleConnect, { data: data?.connectToBank, loading, error }];
};

export const CONNECT_BANK_MUTATION = gql`
  mutation ConnectBank(
    $bankId: Int!
    $tenantId: String!
    $txnImportEnabled: Boolean!
    $startImportDate: DateTime!
    $connectionName: String
    $interfaces: [String!]
  ) {
    connectToBank(
      bankId: $bankId
      tenantId: $tenantId
      txnImportEnabled: $txnImportEnabled
      startImportDate: $startImportDate
      connectionName: $connectionName
      interfaces: $interfaces
    ) {
      url
      id
    }
  }
`;
